import Api from '../Api';
import {
  fetchAllStaticDataForAutofill,
  stripJsonFromPastedText,
  normalizeContactDigits,
  findCityById,
} from './organizationAutofill';

const CONTACT_KEYS = ['phone_number', 'instagram', 'telegram', 'whatsapp', 'viber', 'facebook', 'website'];
const PHONE_DIGIT_CONTACT_KEYS = ['phone_number', 'whatsapp', 'viber'];

/**
 * Страны, города (как в форме) + категории мероприятий с API (актуальные _id).
 */
export async function fetchAllStaticDataForEventAutofill() {
  const base = await fetchAllStaticDataForAutofill();
  const { data } = await Api.getEventCategories();
  const eventCategories = data?.categories || [];
  return {
    countries: base.countries,
    citiesByCountryId: base.citiesByCountryId,
    eventCategories,
  };
}

function findEventCategoryById(eventCategories, id) {
  if (!id) return null;
  return eventCategories.find((c) => c._id === id) || null;
}

/**
 * Частичное заполнение формы «Добавить мероприятие».
 */
export function parseEventAutofillJson(jsonString, staticData) {
  let data;
  try {
    data = JSON.parse(stripJsonFromPastedText(jsonString));
  } catch (e) {
    return { error: 'Не удалось разобрать JSON. Проверьте скобки и кавычки.' };
  }

  if (!data || typeof data !== 'object') {
    return { error: 'Корень JSON должен быть объектом.' };
  }

  const has = (k) => Object.prototype.hasOwnProperty.call(data, k);
  const { citiesByCountryId, eventCategories } = staticData;

  const patches = {};

  if (has('owner_id')) patches.ownerId = String(data.owner_id).trim();
  if (has('name')) patches.name = String(data.name).trim();
  if (has('specialization')) patches.specialization = String(data.specialization).trim();
  if (has('holding_date')) patches.holdingDate = String(data.holding_date).trim();
  if (has('date_start')) patches.dateStart = String(data.date_start).trim();
  if (has('date_end')) patches.dateEnd = String(data.date_end).trim();
  if (has('address')) patches.address = String(data.address).trim();
  if (has('description')) patches.description = String(data.description).trim();

  if (has('contacts') && data.contacts && typeof data.contacts === 'object') {
    patches.contactsPartial = {};
    CONTACT_KEYS.forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(data.contacts, k)) {
        const v = data.contacts[k];
        const raw = v != null ? String(v).trim() : '';
        patches.contactsPartial[k] = PHONE_DIGIT_CONTACT_KEYS.includes(k)
          ? normalizeContactDigits(raw)
          : raw;
      }
    });
  }

  if (has('events_category_id')) {
    const cat = findEventCategoryById(eventCategories, data.events_category_id);
    if (!cat) {
      return { error: 'Категория мероприятия не найдена по events_category_id.' };
    }
    patches.eventCategory = cat;
  }

  if (has('city_id')) {
    const city = findCityById(citiesByCountryId, data.city_id);
    if (!city) {
      return { error: 'Город не найден по city_id.' };
    }
    patches.city = city;
  }

  if (has('coordinates') && data.coordinates && (data.coordinates.lat != null || data.coordinates.lon != null)) {
    patches.coordinates = {
      lat: data.coordinates.lat != null ? String(data.coordinates.lat) : '',
      lon: data.coordinates.lon != null ? String(data.coordinates.lon) : '',
      is_special_point_on_map: Boolean(data.coordinates.is_special_point_on_map),
    };
  }

  return { patches };
}

export function buildEventAutofillReferenceFileContent(staticPayload) {
  const {
    countries,
    citiesByCountryId,
    eventCategories,
  } = staticPayload;

  const referenceData = {
    countries: countries.map((c) => ({ _id: c._id, name: c.name })),
    cities_by_country: countries.map((country) => ({
      country_id: country._id,
      cities: (citiesByCountryId[country._id] || []).map((city) => ({
        _id: city._id,
        name: city.name,
        country_id: city.country_id || country._id,
      })),
    })),
    event_categories: eventCategories
      .slice()
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
      .map((c) => ({
        _id: c._id,
        name: c.name,
        sort: c.sort,
      })),
  };

  const schemaDescription = {
    description: 'Один JSON-объект для формы «Добавить мероприятие». Поля опускай, если в тексте нет данных. Справочники: города и event_categories — в JSON только _id из REFERENCE_DATA.',
    fields: {
      owner_id: '24-символьный hex ID владельца',
      name: 'Название мероприятия',
      events_category_id: '_id из referenceData.event_categories',
      specialization: 'Обязательно, если выбрана категория с name «Другое»; иначе по желанию',
      holding_date: 'Строка для отображения (любой формат текста о дате/времени проведения)',
      date_start: 'YYYY-MM-DD или пусто — влияет на сортировку в приложении',
      date_end: 'YYYY-MM-DD или пусто',
      city_id: '_id города из cities внутри cities_by_country',
      address: 'строка',
      coordinates: { lat: 'число или строка', lon: 'число или строка', is_special_point_on_map: false },
      contacts: 'phone_number, whatsapp, viber — только цифры подряд; остальные поля — строки',
      description: 'строка',
    },
    json_syntax: 'Формат ответа — как у JSON.stringify(объект) в JavaScript: один валидный JSON-текст для JSON.parse(). Допустимы отступы, как JSON.stringify(obj, null, 2). Без markdown и без текста до/после JSON.',
    note: 'Телефоны в contacts.phone_number, whatsapp, viber — только цифры. Если категория «Другое» — обязательно specialization.',
  };

  const prompt = [
    'Ты — помощник по заполнению карточки мероприятия в админке Nomad.',
    '',
    'Порядок работы:',
    '— Сначала человек пришлёт тебе этот файл одним сообщением. На это сообщение ты НЕ генерируешь JSON.',
    '— Если задача понятна, ответь ровно: «Задача ясна, отправь текст для преобразования». Дальше жди следующих сообщений.',
    '— Когда пришлют «сухой» текст о мероприятии, сформируй JSON по схеме.',
    '',
    'REFERENCE_DATA содержит страны, города по странам и event_categories (категории событий: Музыка, Театр, …). Сопоставляй текст с name, в JSON пиши только _id.',
    '',
    'КРИТИЧНО — категория (events_category_id):',
    '— Только строка _id из referenceData.event_categories. Не подставляй название категории вместо id.',
    '— Если по смыслу подходит категория «Другое», обязательно заполни specialization (своя формулировка типа мероприятия).',
    '',
    'КРИТИЧНО — телефоны (contacts.phone_number, contacts.whatsapp, contacts.viber):',
    '— Только цифры подряд; из +7 (999) 123-45-67 → 79991234567.',
    '— instagram, telegram, facebook, website не обрезай до цифр.',
    '',
    'КРИТИЧНО — даты date_start и date_end:',
    '— Формат YYYY-MM-DD (как для input type=date) или пустая строка, если данных нет.',
    '',
    'КРИТИЧНО — формат ответа = как после JSON.stringify в JavaScript:',
    '— Программа вызовет JSON.parse(твой ответ). Один корневой объект, без ```json, без комментариев.',
    '— Двойные кавычки, без запятой после последнего поля, экранирование \\" внутри строк.',
    '',
    'Когда пришёл текст для преобразования:',
    '1) Заполни поля из schemaDescription.fields по смыслу текста.',
    '2) city_id и events_category_id — только из REFERENCE_DATA.',
    '3) Верни один JSON-текст, пригодный для JSON.parse.',
    '',
    'REFERENCE_DATA:',
    JSON.stringify(referenceData, null, 2),
    '',
    'SCHEMA_HINT:',
    JSON.stringify(schemaDescription, null, 2),
  ].join('\n');

  return [
    '══════════════════════════════════════════════════════════════',
    'NOMAD — автозаполнение мероприятия (справочник + инструкция для ИИ)',
    '══════════════════════════════════════════════════════════════',
    '',
    'Как пользоваться (оператору):',
    '1) Сохрани этот файл при необходимости.',
    '2) Первым сообщением отправь в чат с языковой моделью весь файл (или блок REFERENCE_DATA + инструкция ниже).',
    '3) Дождись: «Задача ясна, отправь текст для преобразования».',
    '4) Отправь сухой текст о мероприятии.',
    '5) Скопируй JSON ответа (формат как JSON.stringify — целиком в JSON.parse). Вставь в поле «Автозаполнение» в админке, нажми «Преобразовать».',
    '6) Если JSON не парсится — открой https://jsonviewer.stack.hu/, исправь синтаксис, скопируй обратно.',
    '',
    prompt,
  ].join('\n');
}
